import { OfferNetworkProvider } from "@redotech/crdb-enums/marketing/offer-network-provider";
import { ResolvedOfferConfig } from "@redotech/order-tracking-common/offer-network/offer-placement";
import { OfferNetworkAttributes, toFalconAttributes } from "./offer-attributes";
import { OfferPlacementHandle } from "./offer-placement";
import { loadScriptOnce, unloadScript, waitForGlobal } from "./script-loader";

const FALCON_SDK_URL =
  "https://d6y5cd3imay52.cloudfront.net/sdk/v1/embedded-sdk.js";

/**
 * Falcon locks placementIds globally with no per-placement teardown; reset the
 * SDK when the last placement unmounts (else an SPA remount throws "in use").
 */
let falconMountCount = 0;

function releaseFalconMount(): void {
  falconMountCount = Math.max(0, falconMountCount - 1);
  if (falconMountCount === 0) {
    delete window.FalconAds;
    unloadScript(FALCON_SDK_URL);
  }
}

interface FalconAdsSdk {
  init(config: {
    apiKey: string;
    containerId: string;
    placementId: string;
    attributes?: Record<string, string>;
  }): Promise<void>;
}

declare global {
  interface Window {
    FalconAds?: FalconAdsSdk;
  }
}

export async function mountFalconPlacement(
  config: Extract<
    ResolvedOfferConfig,
    { provider: OfferNetworkProvider.Falcon }
  >,
  attributes: OfferNetworkAttributes,
  container: HTMLElement,
): Promise<OfferPlacementHandle> {
  await loadScriptOnce(FALCON_SDK_URL);
  const sdk = await waitForGlobal(
    () => window.FalconAds?.init && window.FalconAds,
  );
  await sdk.init({
    apiKey: config.apiKey,
    containerId: container.id,
    placementId: config.placementId,
    attributes: toFalconAttributes(attributes),
  });
  falconMountCount += 1;
  let released = false;
  return {
    dispose: () => {
      if (released) {
        return;
      }
      released = true;
      container.replaceChildren();
      releaseFalconMount();
    },
  };
}
